import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/features/auth';
import { UserType } from '@/shared/constants/auth/auth.constant.ts';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import {
  hasPostLoginVerificationCheck,
  clearPostLoginVerificationCheck,
} from '@/shared/libs/post-login-verify';
import { businessApi } from '../api/business.api';
import { businessKeys } from '../api/business.keys';

/**
 * 로그인 직후 1회, 기업 계정이 아직 검증되지 않은 조직을 가지고 있으면
 * 기업검증(기업콘솔) 화면으로 유도한다.
 *
 * - 트리거는 로그인 성공 시 LoginView 가 세팅하는 세션 플래그(one-shot).
 *   → 일반 페이지 이동/새로고침에는 관여하지 않는다("로그인 시" 한정).
 * - 개인 계정/미인증 상태에서는 멤버십 조회 없이 플래그만 소비한다.
 * - 실제 기능 차단은 서버(검증 게이트)와 BusinessLayout 게이팅이 담당한다.
 *   이 훅은 진입 동선만 담당.
 */
export function usePostLoginVerificationRedirect(): void {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const isBusiness = user?.userType === UserType.BUSINESS;

  const flagged = hasPostLoginVerificationCheck();

  // 방금 로그인 + 기업 계정일 때만 멤버십 조회(불필요한 요청 방지).
  const enabled = flagged && isAuthenticated && isBusiness;

  const { data: memberships, isSuccess: groupsLoaded } = useQuery({
    queryKey: businessKeys.myGroups,
    queryFn: businessApi.myGroups,
    enabled,
  });

  const consumed = useRef(false);

  useEffect(() => {
    if (!flagged || consumed.current) return;

    // 개인 계정: 조회 없이 플래그만 소비.
    if (isAuthenticated && !isBusiness) {
      consumed.current = true;
      clearPostLoginVerificationCheck();
      return;
    }

    if (!enabled || !groupsLoaded) return;

    consumed.current = true;
    clearPostLoginVerificationCheck();

    // 미검증 조직을 보유 → 기업검증으로 유도.
    // "승인을 기다리는 계정"이라는 상태는 없다 — 같은 사업자번호의 2번째 가입은
    // 가입 자체가 거부된다.
    const hasUnverifiedOrg = (memberships ?? []).some(
      (m) => m.group && m.group.verificationStatus !== 'VERIFIED',
    );
    if (hasUnverifiedOrg) {
      navigate(PATHS.BUSINESS_VERIFICATION, { replace: true });
    }
  }, [
    flagged,
    enabled,
    isAuthenticated,
    isBusiness,
    groupsLoaded,
    memberships,
    navigate,
  ]);
}
